import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { ReferentialKey } from 'api/referential';
import { useAppSelector } from 'app/hooks';
import { selectReferentialData } from 'features/referentials/selectors/selectReferentialDataByKey';
import { usePanel } from 'hooks/usePanel';
import { GenericLoading } from 'components/ui/Loader';
import { MandantsTunnelsSelector } from 'back-office/shared/components/mandants/MandantsTunnelsSelector';
import { MandantSelection, MandantSelectorPlateforme } from 'back-office/shared/components/mandants/mandantTypes';
import { mandantsArraySchema } from 'back-office/shared/components/mandants/mandantsValidation';
import { PimListItemDto, updatePimMandants } from '../api/pimsApi';

interface PimMandantsPanelProps {
  pim: PimListItemDto;
  onSuccess?: () => void;
}

interface MandantsFormValues {
  mandants: MandantSelection[];
}

const validationSchema = Yup.object({
  mandants: mandantsArraySchema,
});

/**
 * État cible des mandants d'un PIM, tunnels par mandant inclus (granularité
 * paire mandant×tunnel). Le backend resynchronise les habilitations dérivées :
 * pose des nouvelles paires, désactivation en cascade des orphelines — le
 * bilan est remonté en notification.
 */
export const PimMandantsPanel: React.FC<PimMandantsPanelProps> = ({ pim, onSuccess }) => {
  const formikRef = useRef<FormikProps<MandantsFormValues>>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { setPanelActions, closePanel } = usePanel();
  const referentialsData = useAppSelector(selectReferentialData);

  // Plateformes du PIM (id → libellé) pour grouper les mandants proposables.
  const plateformes = useMemo<MandantSelectorPlateforme[]>(() => {
    const orgRefs = referentialsData[ReferentialKey.Plateforme] ?? [];
    return (pim.plateformeIds ?? []).flatMap((id) => {
      const ref = orgRefs.find((r) => r.id === id);
      return ref ? [{ id, label: ref.label }] : [{ id, label: id }];
    });
  }, [pim.plateformeIds, referentialsData]);

  // Libellés des mandants déjà en place (affichage des cartes en édition).
  const initialLabels = useMemo(
    () =>
      Object.fromEntries(
        (pim.mandants ?? []).flatMap((m) => (m.label ? [[m.courtierId, m.label]] : [])),
      ),
    [pim.mandants],
  );

  useEffect(() => {
    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      onSubmit: () => {
        formikRef.current?.submitForm();
      },
    });
  }, [setPanelActions]);

  const handleSubmit = async (values: MandantsFormValues) => {
    setIsLoading(true);
    try {
      const response = await updatePimMandants(pim.courtierId, {
        mandants: values.mandants,
      });

      if (response?.isSuccess) {
        if (response.notification?.message) {
          // Bilan des habilitations posées/désactivées → informer l'utilisateur
          toast.warning(response.notification.message);
        } else {
          toast.success('Mandants mis à jour');
        }
        onSuccess?.();
        closePanel();
      } else if (response?.errorMessage) {
        toast.error(response.errorMessage);
      }
    } catch (error) {
      console.error('Error updating PIM mandants:', error);
      toast.error('Erreur lors de la mise à jour des mandants');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      {isLoading && <GenericLoading loading isParentSized />}

      <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
        L&apos;accès aux tunnels se donne PAR mandant : décocher un tunnel ou
        retirer un mandant désactive automatiquement les habilitations du PIM
        qui ne sont plus couvertes.
      </div>

      <Formik<MandantsFormValues>
        initialValues={{
          mandants: (pim.mandants ?? []).map((m) => ({
            courtierId: m.courtierId,
            tunnelIds: m.tunnelIds ?? [],
          })),
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        <Form>
          {/* Courtiers des plateformes du PIM, groupés par plateforme */}
          <MandantsTunnelsSelector
            name="mandants"
            plateformes={plateformes}
            initialLabels={initialLabels}
          />
        </Form>
      </Formik>
    </div>
  );
};
